import { useEffect, useRef, useState } from 'react';
import { computeSpectrogram, paintSpectrogram } from '../lib/audio/spectrogram.js';
import { formatDuration } from '../lib/audio/prepare.js';

interface Props {
  samples: Float32Array;
  sampleRateHz: number;
  durationMs: number;
  maxFrequencyHz?: number;
}

/**
 * Renders a recording as a spectrogram.
 *
 * This is the first thing most contributors will have seen of their own
 * animal's voice as a picture, and it is the single strongest reason to come
 * back — the "give something back" layer the plan identifies as the retention
 * phase. Without it, week-two usage goes to zero however good the capture is.
 */
export function Spectrogram({ samples, sampleRateHz, durationMs, maxFrequencyHz = 12000 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    setRendering(true);

    // Yield a frame first so the browser can paint the loading state — the FFT
    // is synchronous and would otherwise block before anything appeared.
    const handle = requestAnimationFrame(() => {
      if (cancelled) return;

      const data = computeSpectrogram(samples, sampleRateHz);

      // Match the backing store to the displayed size, at device resolution.
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));

      paintSpectrogram(canvas, data, maxFrequencyHz);
      setRendering(false);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(handle);
    };
  }, [samples, sampleRateHz, maxFrequencyHz]);

  return (
    <figure className="scope" style={{ margin: 0 }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`Spectrogram of a ${formatDuration(durationMs)} recording. Brighter amber marks louder sound; teal is the quieter context around it.`}
      />
      <figcaption className="scope-axis">
        <span>0:00</span>
        <span>{rendering ? 'rendering…' : `0–${Math.round(maxFrequencyHz / 1000)} kHz`}</span>
        <span>{formatDuration(durationMs)}</span>
      </figcaption>
    </figure>
  );
}
